import React, {useState, useEffect, useRef} from 'react';
import './BackOfficeMenu.css';
import {usePermissions} from '../../utils/usePermissions';
import {MENU_SECTIONS} from '../../utils/navigationUtils';
import NotificationBell from './NotificationBell';

/* ─────────────────────────────────────────────
   Menu structure with icons + grouped sections
───────────────────────────────────────────── */

const STOCK_KEYS = ['produitsAdmin', 'packsAdmin', 'stockmanagement', 'stockstatus', 'inventaire', 'mouvements-inventaire', 'warehouse3d'];

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */
const BackOfficeMenu = ({onNavigate, onLogout, currentPage}) => {
  const {hasPermission, typeUser} = usePermissions();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false); // sidebar collapse on desktop
  const [stockOpen, setStockOpen] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const sidebarRef = useRef(null);
  
  /* Sync collapsed state to body for global CSS access */
  useEffect(() => {
    if (!isMobile) {
      document.body.setAttribute('data-sidebar-collapsed', isCollapsed);
    } else {
      document.body.removeAttribute('data-sidebar-collapsed');
    }
  }, [isCollapsed, isMobile]);

  /* Responsive detection */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  /* Auto-open stock section when on a stock page */
  useEffect(() => {
    if (STOCK_KEYS.includes(currentPage)) setStockOpen(true);
  }, [currentPage]);

  /* Close mobile menu on outside click */
  useEffect(() => {
    if (!isMobile || !isMenuOpen) return;
    const handler = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isMobile, isMenuOpen]);

  const navigate = (page) => {
    setIsMenuOpen(false);

    // Redirection spéciale pour les modérateurs demandée par l'utilisateur
    // "Juste qui ont accès" -> on redirige vers le profil frontend si le modérateur possède la permission
    if (typeUser === 'moderateur') {
      const isSteg = page === 'steg' && hasPermission('clients', 'read');
      const isContacts = page === 'contacts' && hasPermission('utilisateurs', 'read');
      const isEntreprises = page === 'entreprises' && hasPermission('utilisateurs', 'read');

      if (isSteg || isContacts || isEntreprises) {
        onNavigate('profile');
        return;
      }
    }

    onNavigate(page);
  };

  const handleLogout = () => {
    setIsMenuOpen(false);
    onLogout();
  };

  const collapsed = !isMobile && isCollapsed;

  /* ── render a single item ── */
  const renderItem = (item, isSubItem = false) => {
    // Masquer certains items pour les modérateurs (redirection vers front ou non souhaité)
    if (typeUser === 'moderateur' && (item.key === 'steg' || item.key === 'contacts' || item.key === 'entreprises')) {
      return null;
    }

    // Vérification de permission frontend
    if (item.key === 'actualites-newsletter') {
      if (!hasPermission('actualites', 'read') && !hasPermission('newsletter', 'read')) return null;
    } else if (item.permission) {
      const [module, action] = item.permission.split(':');
      if (!hasPermission(module, action || 'read')) return null;
    }

    const isActive = currentPage === item.key;
    return (
      <button
        key={item.key}
        id={`menu-item-${item.key}`}
        className={`bom-item ${isActive ? 'bom-active' : ''} ${isSubItem ? 'bom-sub-item' : ''}`}
        onClick={() => navigate(item.key)}
        onMouseEnter={() => collapsed && setActiveTooltip(item.key)}
        onMouseLeave={() => setActiveTooltip(null)}
        aria-current={isActive ? 'page' : undefined}
        title={collapsed ? item.label : undefined}
      >
        <span className="bom-icon">{item.icon}</span>
        {!collapsed && <span className="bom-label">{item.label}</span>}
        {isActive && !isSubItem && <span className="bom-active-bar" />}
        {collapsed && activeTooltip === item.key && (
          <span className="bom-tooltip">{item.label}</span>
        )}
      </button>
    );
  };

  /* ── render a section ── */
  const renderSection = (sec, idx) => {
    // Filtrer les items autorisés AVEC la même logique que renderItem
    const authorizedItems = sec.items.filter(item => {
      // 1. Cacher les items spécifiques au modérateur
      if (typeUser === 'moderateur' && (item.key === 'steg' || item.key === 'contacts' || item.key === 'entreprises')) {
        return false;
      }

      // 2. Vérifier les permissions
      if (item.permission) {
        const [module, action] = item.permission.split(':');
        return hasPermission(module, action || 'read');
      }
      return true;
    });

    // Si aucun item autorisé dans cette section, on masque tout (item + label de section)
    if (authorizedItems.length === 0) return null;

    const isStockSection = sec.collapsible && sec.groupKey === 'stock';
    const hasActiveChild = authorizedItems.some(i => i.key === currentPage);

    return (
      <div className="bom-section" key={idx}>
        {sec.section && !collapsed && (
          <div className="bom-section-label">
            {sec.collapsible ? (
              <button
                className={`bom-section-toggle ${hasActiveChild ? 'has-active' : ''}`}
                onClick={() => isStockSection && setStockOpen(p => !p)}
              >
                <span>{sec.section}</span>
                <span className={`bom-chevron ${isStockSection && stockOpen ? 'up' : ''}`}>›</span>
              </button>
            ) : (
              <span>{sec.section}</span>
            )}
          </div>
        )}

        {sec.collapsible ? (
          <>
            {/* Collapsed sidebar → always show items */}
            {collapsed && authorizedItems.map(item => renderItem(item))}
            {/* Expanded sidebar → accordion */}
            {!collapsed && (
              <div className={`bom-accordion ${isStockSection && stockOpen ? 'open' : ''}`}>
                <div className="bom-accordion-inner">
                  {authorizedItems.map(item => renderItem(item, true))}
                </div>
              </div>
            )}
          </>
        ) : (
          authorizedItems.map(item => renderItem(item))
        )}
      </div>
    );
  };

  return (
    <>
      {/* Notification Bell (Fixed Top Right) */}
      <div style={{ position: 'fixed', top: '20px', right: '30px', zIndex: 1050 }}>
        <NotificationBell />
      </div>

      {/* Mobile hamburger */}
      {isMobile && (
        <button
          id="backoffice-menu-toggle"
          className={`bom-toggle ${isMenuOpen ? 'open' : ''}`}
          onClick={() => setIsMenuOpen(p => !p)}
          aria-label="Ouvrir le menu"
        >
          <span /><span /><span />
        </button>
      )}

      {/* Mobile backdrop */}
      {isMobile && isMenuOpen && (
        <div className="bom-backdrop" onClick={() => setIsMenuOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`bom-sidebar ${collapsed ? 'collapsed' : ''} ${isMobile && isMenuOpen ? 'mobile-open' : ''}`}
        aria-label="Navigation BackOffice"
      >
        {/* Header */}
        <div className="bom-header">
          <div className="bom-logo">
            <span className="bom-logo-icon">⚡</span>
            {!collapsed && (
              <div className="bom-logo-text">
                <span className="bom-logo-title">SASTRA</span>
                <span className="bom-logo-sub">BackOffice</span>
              </div>
            )}
          </div>
          {/* Desktop collapse button */}
          {!isMobile && (
            <button
              className="bom-collapse-btn"
              onClick={() => setIsCollapsed(p => !p)}
              title={collapsed ? 'Agrandir' : 'Réduire'}
              aria-label="Réduire/Agrandir le menu"
            >
              {collapsed ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
              )}
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="bom-nav">
          {MENU_SECTIONS.map((sec, idx) => renderSection(sec, idx))}
        </nav>


        {/* Footer */}
        <div className="bom-footer">
          <div className={`bom-footer-divider ${collapsed ? 'collapsed' : ''}`} />
          <button
            id="menu-item-home"
            className="bom-item bom-item-site"
            onClick={() => navigate('home')}
            onMouseEnter={() => collapsed && setActiveTooltip('home')}
            onMouseLeave={() => setActiveTooltip(null)}
          >

            {!collapsed && <span className="bom-label">Retour au site</span>}
            {collapsed && activeTooltip === 'home' && (
              <span className="bom-tooltip">Retour au site</span>
            )}
          </button>

          <button
            id="menu-item-logout"
            className="bom-item bom-item-logout"
            onClick={handleLogout}
            onMouseEnter={() => collapsed && setActiveTooltip('logout')}
            onMouseLeave={() => setActiveTooltip(null)}
          >

            {!collapsed && <span className="bom-label">Déconnexion</span>}
            {collapsed && activeTooltip === 'logout' && (
              <span className="bom-tooltip">Déconnexion</span>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export default BackOfficeMenu;